import type { LucideIcon } from 'lucide-react';
import { Activity, ArrowLeftRight, Cpu, DoorOpen, Keyboard, LayoutGrid, Lightbulb, ListTree, Rows3, SlidersHorizontal, Terminal } from 'lucide-react';

/** Application routes the RCU feature needs by name. */
export const ROUTE = {
  CONTROLS: '/controls',
  TECHNICIAN: '/technician',
} as const;

export type RoutePath = (typeof ROUTE)[keyof typeof ROUTE];

/**
 * The console's two halves: what the device is (configured on the bench, stored
 * on the tablet) and what it is doing right now (read and driven over the link).
 */
export const CONSOLE_GROUP = {
  DEFINE: 'define',
  DEVICE: 'device',
} as const;

export type ConsoleGroupId = (typeof CONSOLE_GROUP)[keyof typeof CONSOLE_GROUP];

export const CONSOLE_GROUP_LABEL = {
  [CONSOLE_GROUP.DEFINE]: 'Configure',
  [CONSOLE_GROUP.DEVICE]: 'Device',
} as const satisfies Record<ConsoleGroupId, string>;

/** Sections of the technician console. */
export const CONSOLE_TAB = {
  OVERVIEW: 'overview',
  OUTPUTS: 'outputs',
  RAILS: 'rails',
  LOADS: 'loads',
  ROOMS: 'rooms',
  TRANSFER: 'transfer',
  STATUS: 'status',
  KEYPADS: 'keypads',
  COMMANDS: 'commands',
  MAPPINGS: 'mappings',
  DIAGNOSTICS: 'diagnostics',
} as const;

export type ConsoleTabId = (typeof CONSOLE_TAB)[keyof typeof CONSOLE_TAB];

export interface ConsoleTabSpec {
  readonly id: ConsoleTabId;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly group: ConsoleGroupId;
}

export const CONSOLE_TABS = [
  { id: CONSOLE_TAB.OVERVIEW, label: 'Overview', icon: LayoutGrid, group: CONSOLE_GROUP.DEFINE },
  { id: CONSOLE_TAB.OUTPUTS, label: 'RCU outputs', icon: Cpu, group: CONSOLE_GROUP.DEFINE },
  { id: CONSOLE_TAB.RAILS, label: 'DIN rails', icon: Rows3, group: CONSOLE_GROUP.DEFINE },
  { id: CONSOLE_TAB.LOADS, label: 'Loads', icon: Lightbulb, group: CONSOLE_GROUP.DEFINE },
  { id: CONSOLE_TAB.ROOMS, label: 'Rooms', icon: DoorOpen, group: CONSOLE_GROUP.DEFINE },
  {
    id: CONSOLE_TAB.TRANSFER,
    label: 'Import / export',
    icon: ArrowLeftRight,
    group: CONSOLE_GROUP.DEFINE,
  },
  { id: CONSOLE_TAB.STATUS, label: 'Status', icon: Activity, group: CONSOLE_GROUP.DEVICE },
  { id: CONSOLE_TAB.KEYPADS, label: 'Keypads', icon: Keyboard, group: CONSOLE_GROUP.DEVICE },
  {
    id: CONSOLE_TAB.COMMANDS,
    label: 'Commands',
    icon: SlidersHorizontal,
    group: CONSOLE_GROUP.DEVICE,
  },
  { id: CONSOLE_TAB.MAPPINGS, label: 'Mappings', icon: ListTree, group: CONSOLE_GROUP.DEVICE },
  { id: CONSOLE_TAB.DIAGNOSTICS, label: 'Diagnostics', icon: Terminal, group: CONSOLE_GROUP.DEVICE },
] as const satisfies readonly ConsoleTabSpec[];

/** The groups, in the order the header shows them. */
export const CONSOLE_GROUPS = [CONSOLE_GROUP.DEFINE, CONSOLE_GROUP.DEVICE] as const;
